import React, { useState, Fragment } from 'react';
import { Box } from '@mui/system';
import { CustomIcon } from 'components/CustomIcon';
import TypographyComponent from 'components/Typography/Typography';
import * as Styled from '../ProductCard.styles';
import {
  ProjectDropdownProps,
  ProjectDropdownVariant,
  ProjectDropdownVariantsEnum,
} from '../ProductCard.types';

const PROJECTS_NUMBER_OVERFLOW = 3;
export interface ProjectProps {
  label: string;
  id: number;
  isLoading?: boolean;
  isAddedToCart?: boolean;
  isActive?: boolean;
}

const promoteSelectedProject = (data: ProjectProps[], idToFind?: number) => {
  const newProjectList = [...data];
  newProjectList.some(
    (item, idx) =>
      item.id == idToFind &&
      newProjectList.unshift(newProjectList.splice(idx, 1)[0]),
  );
  return newProjectList;
};

const ProjectDropdown: React.FC<ProjectDropdownProps> = ({
  shouldShow,
  selectedProject,
  onAddToBag,
  onProjectClick,
  projects,
  onMouseEnter,
  onMouseLeave,
  onCreateProject,
  isContained,
  preLabel,
  variant = ProjectDropdownVariant.CONTAINED,
  isAddToCartLoading = false,
  showExtraInfo = false,
  hideAddToBag = false,
  maxWidth,
  className,
  isAddedToCart = false,
  dropdownClassName,
  entityId = undefined,
  dropdownVariant = ProjectDropdownVariantsEnum.FULL_SELECT,
  createProjectButtonLabel = 'Create Project',
  searchPlaceholderLabel = 'Search',
  minWidth,
}) => {
  const [shouldShowMenu, setShouldShowMenu] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const shouldShowProjectSearch = projects.length > PROJECTS_NUMBER_OVERFLOW;
  const filteredProjects = projects.filter((project) =>
    project.label
      ?.toLocaleLowerCase()
      ?.includes(searchInputValue.toLocaleLowerCase()),
  );

  const handleDropdownButtonClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isAddToCartLoading) {
        return;
      }

      e.stopPropagation();
      onAddToBag?.(selectedProject?.id);
    },
    [isAddToCartLoading, onAddToBag, selectedProject?.id],
  );

  const handleProjectTitleClick = (selectedProjectId?: number) => {
    onProjectClick?.(selectedProjectId);
    setShouldShowMenu(false);
  };

  const handleMouseEnter = () => {
    onMouseEnter?.();
  };

  const handleMouseLeave = () => {
    onMouseLeave?.();
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!shouldShowMenu) {
      setShouldShowMenu(true);
    }
  };

  const handleMenuMouseLeave = () => {
    setShouldShowMenu(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
  };

  const selectedItemFirstProjects = promoteSelectedProject(
    filteredProjects,
    selectedProject?.id,
  );

  const dropdownMenuContentJsx = (
    <Fragment>
      {shouldShowProjectSearch && (
        <Styled.ProjectsSearchContainer>
          <CustomIcon name="search" dense size={1} />
          <Styled.ProjectsSearchInput
            placeholder={searchPlaceholderLabel}
            spellCheck={false}
            value={searchInputValue}
            onChange={handleSearchInputChange}
          />
        </Styled.ProjectsSearchContainer>
      )}
      <Styled.ProjectsSearchScrollContainer
        showProjectsSearch={shouldShowProjectSearch}
      >
        {selectedItemFirstProjects.map((project) => (
          <Styled.DropdownProjectLine
            isDropdownOnly={
              dropdownVariant === ProjectDropdownVariantsEnum.DROPDOWN_ONLY
            }
            key={project.id}
            role="button"
            onClick={() => handleProjectTitleClick(project.id)}
          >
            <TypographyComponent variant="tiny" flex="1" component="p">
              {project.label}
            </TypographyComponent>
            {project.id === selectedProject?.id && (
              <CustomIcon dense name="check" size={0.8} />
            )}
          </Styled.DropdownProjectLine>
        ))}
      </Styled.ProjectsSearchScrollContainer>
      <Styled.CheckoutDivider />
      <Box width="100%">
        <Styled.CreateProjectButton
          isDropdownOnly={
            dropdownVariant === ProjectDropdownVariantsEnum.DROPDOWN_ONLY
          }
          fullWidth
          onClick={onCreateProject}
          label={createProjectButtonLabel}
          variant="text"
        />
      </Box>
    </Fragment>
  );
  const dropdownMenuJsx = (
    <Styled.CheckoutMenuContainer
      maxWidth={maxWidth}
      isContained={isContained}
      onMouseLeave={handleMenuMouseLeave}
      showExtraInfo={showExtraInfo}
      variant={variant}
      className={dropdownClassName}
    >
      {dropdownMenuContentJsx}
    </Styled.CheckoutMenuContainer>
  );

  if (dropdownVariant === ProjectDropdownVariantsEnum.DROPDOWN_ONLY) {
    return dropdownMenuContentJsx;
  }

  const dropdownMenuToRenderJsx = shouldShowMenu ? (
    dropdownMenuJsx
  ) : (
    <Fragment></Fragment>
  );
  const labelMaxWidth =
    maxWidth == undefined || isNaN(maxWidth) ? undefined : maxWidth - 40;

  return shouldShow ? (
    <Styled.ProjectDropdownContainer
      isContained={isContained}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleDropdownClick}
      variant={variant}
      className={className}
    >
      {dropdownMenuToRenderJsx}
      <Styled.DropDownWrapper
        withBag={!hideAddToBag}
        id={
          entityId != null
            ? `search_item_${entityId}_project_select_button`
            : undefined
        }
        data-testid="project-select-button"
      >
        {preLabel != null && (
          <Styled.SelectedProjectPreLabel dropdownVariant={variant}>
            {preLabel}
          </Styled.SelectedProjectPreLabel>
        )}
        <Styled.SelectedProjectLabel
          minWidth={minWidth}
          maxWidth={labelMaxWidth}
          variant="body"
          dropdownVariant={variant}
        >
          {selectedProject?.label}
        </Styled.SelectedProjectLabel>
        <CustomIcon size={0.7} name="down" />
      </Styled.DropDownWrapper>
      {!hideAddToBag && (
        <Styled.DropdownButton
          id={
            entityId != null ? `search_item_${entityId}_cart_button` : undefined
          }
          onClick={handleDropdownButtonClick}
          isStartIconCircularProgress={isAddToCartLoading}
          size="large"
          isAddedToCart={isAddedToCart}
          iconName={isAddedToCart ? 'check' : 'cart-material'}
          dense
          color="primary"
        />
      )}
    </Styled.ProjectDropdownContainer>
  ) : (
    <Fragment></Fragment>
  );
};

export default ProjectDropdown;
